import { ListingItem, StorageHelper } from '@smg-real-estate/hg-search-libs';
import { defineStore } from 'pinia';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { RouteLocation } from 'vue-router';

import { getAuthToken } from '@/helpers/Authentication/authClient';
import {
  createFavouritesList,
  getFavouritesList,
  removeFavouritesListItem,
  updateFavouritesList,
} from '@/helpers/Favourites/Favourites';
import { StorageNamespace } from '@/helpers/storage-namespaces';
import { isLayoutSolutionActive } from '@/layout-solution/layout-solution';
import { HgLocalStorageKey } from '@/models';
import {
  FavouriteListingDescriptor,
  FavouritesModuleStatus,
  FavouritesState,
  SaveFavouriteActionPayload,
} from '@/store/types/favourites-state';
import { trackFavouriteAddTry, trackFavouriteSave } from '@/trackers/Favourite/Favourite.tracking';

const storageHelper = new StorageHelper(StorageNamespace.favourites);

function getFavouritesFromLocalStorage(): FavouriteListingDescriptor[] {
  return storageHelper.getLocalStorageItem(HgLocalStorageKey.FAVOURITE_LISTINGS) || [];
}

interface FavouritesStore {
  isFavouritesEnabled: Ref<FavouritesState['isFavouritesEnabled']>;
  favouritesModuleStatus: Ref<FavouritesModuleStatus>;
  favouriteListingDescriptors: Ref<FavouriteListingDescriptor[]>;
  favouritesListId: Ref<FavouritesState['favouritesListId']>;
  isFavouritesButtonDisabled: Ref<FavouritesState['isFavouritesButtonDisabled']>;
  favouriteListingRecords: Ref<ListingItem[]>;
  unavailableListingIds: Ref<string[]>;

  // getters
  isFavouritesInitialized: ComputedRef<boolean>;
  isListingFavourite: (listingId: string) => boolean;

  // mutations
  setFavouritesModuleStatus: (status: FavouritesModuleStatus) => void;
  setIsFavouritesEnabled: (enabled: boolean) => void;
  setFavouriteListingDescriptors: (descriptors: FavouriteListingDescriptor[]) => void;
  setFavouriteListingRecords: (records: ListingItem[]) => void;
  setUnavailableListingIds: (ids: string[]) => void;
  setFavouritesListId: (id: string | null) => void;
  setFavouritesButtonDisabled: (isDisabled: boolean) => void;
  saveFavourite: (payload: SaveFavouriteActionPayload) => Promise<void>;
  removeFavourite: (listingId: string) => Promise<void>;

  // actions
  fetchFavouritesListId: (options?: { listingId?: string }) => Promise<string | null>;
  fetchFavouriteListingDescriptors: () => Promise<void>;
}

// eslint-disable-next-line max-statements
export const useFavouritesStore = defineStore('favourites', (): FavouritesStore => {
  const isFavouritesEnabled = ref<FavouritesState['isFavouritesEnabled']>(false);
  const favouritesModuleStatus = ref<FavouritesModuleStatus>(FavouritesModuleStatus.UNINITIALIZED);
  const favouriteListingDescriptors = ref<FavouriteListingDescriptor[]>([]);
  const favouritesListId = ref<FavouritesState['favouritesListId']>(null);
  const isFavouritesButtonDisabled = ref<FavouritesState['isFavouritesButtonDisabled']>(false);
  const favouriteListingRecords = ref<ListingItem[]>([]);
  const unavailableListingIds = ref<string[]>([]);

  const isFavouritesInitialized = computed(
    () => favouritesModuleStatus.value !== FavouritesModuleStatus.UNINITIALIZED,
  );

  const isListingFavourite = (listingId: string): boolean =>
    favouriteListingDescriptors.value.some((descriptor) => descriptor.listingId === listingId);

  function setFavouritesModuleStatus(status: FavouritesModuleStatus) {
    favouritesModuleStatus.value = status;
  }

  function setIsFavouritesEnabled(enabled: boolean) {
    isFavouritesEnabled.value = enabled;
  }

  function setFavouriteListingDescriptors(descriptors: FavouriteListingDescriptor[]): void {
    if (Array.isArray(descriptors)) {
      favouriteListingDescriptors.value = descriptors;
    } else {
      console.warn('Attempted to commit invalid favouriteListingDescriptors', descriptors);
    }
  }

  function setFavouriteListingRecords(records: ListingItem[]): void {
    favouriteListingRecords.value = records;
  }

  function setUnavailableListingIds(ids: string[]): void {
    unavailableListingIds.value = ids;
  }

  function setFavouritesListId(id: string | null): void {
    favouritesListId.value = id;
  }

  function setFavouritesButtonDisabled(isDisabled: boolean): void {
    isFavouritesButtonDisabled.value = isDisabled;
  }

  async function saveFavourite(payload: SaveFavouriteActionPayload): Promise<void> {
    setFavouritesButtonDisabled(true);
    const isAuthenticated = await getAuthToken();

    if (isAuthenticated) {
      const listId =
        favouritesListId.value || (await fetchFavouritesListId({ listingId: payload.listingId }));
      await updateFavouritesList(listId, payload.listingId);
      await fetchFavouriteListingDescriptors();
      trackFavouriteSave({
        interactionContainer: payload.pageInteractionName,
        listingId: payload.listingId,
        listingItem: payload.listingItem,
      });
    } else {
      trackFavouriteAddTry(payload.pageInteractionName, payload.listingId);
    }
    setFavouritesButtonDisabled(false);
  }

  async function removeFavourite(listingId: string): Promise<void> {
    const isAuthenticated = await getAuthToken();

    if (isAuthenticated) {
      setFavouritesButtonDisabled(true);
      const listId = favouritesListId.value || (await fetchFavouritesListId({ listingId }));
      await removeFavouritesListItem(listId, listingId);
      await fetchFavouriteListingDescriptors();
      setFavouritesButtonDisabled(false);
    }
  }

  async function fetchFavouritesListId({ listingId }: { listingId?: string } = {}): Promise<
    string | null
  > {
    if (!favouritesListId.value) {
      const favouritesList = await getFavouritesList();
      if (favouritesList && favouritesList.length > 0) {
        const listId = favouritesList[0].identifier;
        setFavouritesListId(listId);
        return listId;
      }

      const newFavouritesListId = await createFavouritesList(listingId || '');
      setFavouritesListId(newFavouritesListId?.identifier ?? null);
      return newFavouritesListId?.identifier ?? null; // default value is null
    }

    return favouritesListId.value;
  }

  async function fetchFavouriteListingDescriptors(): Promise<void> {
    const favouritesList = await getFavouritesList();
    if (favouritesList && favouritesList.length > 0) {
      const descriptors = favouritesList[0].listItems.map((item) => ({
        listingId: item.itemId,
        savedOn: Date.now(),
      }));
      setFavouriteListingDescriptors(descriptors);
    } else {
      console.warn('Fetched favourites list is empty or undefined');
    }
  }

  return {
    // state
    isFavouritesEnabled,
    favouritesModuleStatus,
    favouriteListingDescriptors,
    favouritesListId,
    isFavouritesButtonDisabled,
    favouriteListingRecords,
    unavailableListingIds,

    // getters
    isFavouritesInitialized,
    isListingFavourite,

    // mutations
    setFavouritesModuleStatus,
    setIsFavouritesEnabled,
    setFavouriteListingDescriptors,
    setFavouriteListingRecords,
    setUnavailableListingIds,
    setFavouritesListId,
    setFavouritesButtonDisabled,
    saveFavourite,
    removeFavourite,

    // actions
    fetchFavouritesListId,
    fetchFavouriteListingDescriptors,
  };
});

export async function initializeFavouritesStore(route: RouteLocation): Promise<void> {
  const favouritesStore = useFavouritesStore();
  let isFavouritesEnabled = false;
  let persistedFavouriteListingDescriptors: FavouriteListingDescriptor[] = [];

  try {
    isFavouritesEnabled =
      storageHelper.isLocalStorageAvailable() && !isLayoutSolutionActive(route.query);

    const authToken = await getAuthToken();
    if (authToken) {
      await favouritesStore.fetchFavouriteListingDescriptors();
      persistedFavouriteListingDescriptors = favouritesStore.favouriteListingDescriptors;
    } else {
      persistedFavouriteListingDescriptors = getFavouritesFromLocalStorage();
    }
  } catch (e) {
    console.error('Error during initialization:', e);
  }

  if (Array.isArray(persistedFavouriteListingDescriptors)) {
    favouritesStore.setFavouriteListingDescriptors(persistedFavouriteListingDescriptors);
  } else {
    console.warn(
      'Invalid persistedFavouriteListingDescriptors:',
      persistedFavouriteListingDescriptors,
    );
  }

  favouritesStore.setFavouritesModuleStatus(FavouritesModuleStatus.INITIALIZED);
  favouritesStore.setIsFavouritesEnabled(isFavouritesEnabled);
  favouritesStore.setFavouritesListId(null);
  favouritesStore.setFavouritesButtonDisabled(false);
}
